import { memo } from "react";
import { AutoSizer, InfiniteLoader, List, WindowScroller } from "react-virtualized";
import { Loader, Segment } from "semantic-ui-react";
import { useZonedDateTime } from "../../../../../../../../components/global_components/ZonedDateTime";
import { useClient } from "../../../../../../../../hooks/useClient";
import { GetNotifyNotificationContentSearchResponse } from "../../../../../../../../queries/notify";
import useSite from "../../../../../../../permits/components/inspections/InspectionPanel/useSite";
import { useNotifyNotificationsCardInfiniteList } from "../../../hooks/useNotifyNotificationsCardInfiniteList";
import { Link } from "react-router-dom";

type NotificationContent = GetNotifyNotificationContentSearchResponse["data"][number];

export const NotifyNotificationsTemplateCardInfiniteList = memo(({ query }: { query: string }) => {
  const {
    notifyContent,
    notifyContent2D,
    total2D,
    isNotificationContentFetching,
    loadMoreRows,
    scrollElement,
    columnCount,
  } = useNotifyNotificationsCardInfiniteList({
    filters: { query, departmentGroupId: null, status: null, subscriptionGroupIds: [] },
  });

  if (isNotificationContentFetching && notifyContent.length === 0) {
    return (
      <Segment className='tw-h-[60vh]' basic>
        <Loader active />
      </Segment>
    );
  }

  return (
    <InfiniteLoader
      isRowLoaded={({ index }) => !!notifyContent2D[index]}
      loadMoreRows={loadMoreRows}
      rowCount={total2D}
      threshold={10}
    >
      {({ onRowsRendered, registerChild }) => (
        <WindowScroller scrollElement={scrollElement}>
          {({ height, isScrolling, onChildScroll, scrollTop }) => (
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  autoHeight
                  height={height}
                  isScrolling={isScrolling}
                  onScroll={onChildScroll}
                  scrollTop={scrollTop}
                  onRowsRendered={onRowsRendered}
                  ref={registerChild}
                  rowCount={notifyContent2D.length}
                  rowHeight={150}
                  rowRenderer={({ index, key, style }) => (
                    <div key={key} style={{ ...style }}>
                      <div
                        className='tw-grid tw-h-full tw-gap-3 tw-pb-3'
                        style={{
                          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                        }}
                      >
                        {notifyContent2D[index].map((content) => (
                          <NotifyNotificationsTemplateCard key={content.id} content={content} />
                        ))}
                      </div>
                    </div>
                  )}
                  width={width}
                />
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      )}
    </InfiniteLoader>
  );
});

function NotifyNotificationsTemplateCard({ content }: { content: NotificationContent }) {
  const site = useSite();
  const { name: siteName } = useClient(site);

  const formattedUpdatedAt = useZonedDateTime({
    value: content.updatedAt,
    formatString: "MMM d, yyyy",
    disableAutoTimezoneIndicator: true,
  });

  const templateSearchParams = new URLSearchParams();
  templateSearchParams.set("template-type", "notification");
  templateSearchParams.set("template-id", `${content.id}`);

  return (
    <Link
      to={{
        pathname: `/${site}/notify/notifications/create`,
        search: `?${templateSearchParams.toString()}`,
      }}
    >
      <div className='tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-lg tw-p-3 tw-text-text hover:tw-bg-white-background'>
        <div className='tw-aspect-square tw-h-[126px]'>
          {
            content.mainImageUrl && (
              <img
                src={content.mainImageUrl}
                alt=''
                className='tw-aspect-square tw-h-[126px] tw-rounded-[4px] tw-object-cover'
              />
            ) /* TODO: also parse out the alt text */
          }
        </div>
        <div className='tw-flex tw-w-full tw-flex-col tw-gap-1'>
          <h4 className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>
            From {content.departmentSentFrom?.name ?? `${siteName}`}
          </h4>
          <div>
            <h2 className='tw-m-0 tw-line-clamp-1 tw-text-xl'>{content.headline}</h2>
            <h3 className='tw-m-0 tw-line-clamp-1 tw-text-lg'>{content.tagline}</h3>
          </div>
          <h4 className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>{formattedUpdatedAt}</h4>
        </div>
      </div>
    </Link>
  );
}
